import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  message,
  Button,
  Typography,
  Row,
  Col,
  Spin,
  Alert,
  Tag,
  Input,
  Space,
  Descriptions,
  Drawer,
  Modal,
} from "antd";
import { UndoOutlined } from "@ant-design/icons";
import { motion } from 'framer-motion';

import {
  useFetchTransfertById,
  useFetchItemsInTransfert,
  useValidateTransfert,
  useSaveTransfert,
} from "../../hooks/transfertHooks";
import AddItemToTransfertForm from './AddItemToTransfertForm';
import NestedAchatTable from '../achat/NestedAchatTable';
import EditableDateField from '../../utils/EditableDateField';

const { Title, Text } = Typography;

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString() : "Not provided";

const EditableField = ({
  value,
  onChange,
  userPermissions,
  transfertStatus
}) => {
  const canEdit = currentCan('transfers', 'update') && (userPermissions?.isManager || (!transfertStatus?.VALIDER && !transfertStatus?.TRANSFERER));

  const handleChange = (e) => {
    if (!canEdit) return;
    onChange(e.target.value);
  };

  return (
    <Input.TextArea
      value={value}
      onChange={handleChange}
      disabled={!canEdit}
      autoSize={{ minRows: 2, maxRows: 6 }}
    />
  );
};

const SingleTransfert = ({
  id,
  onClose,
  userPermissions = {isManager: false},
  onChangesSaved,
  onChangesDetected
}) => {
  const {
    data: initialTransfert,
    loading: loadingTransfert,
    error: errorTransfert,
    refetch,
  } = useFetchTransfertById(id);
  const {
    items: initialItems,
    loading: loadingItems,
    error: errorItems,
    refetch: refetchItems,
  } = useFetchItemsInTransfert(id);
  const { validate, error: validateError } = useValidateTransfert();
  const { save, error: saveError } = useSaveTransfert();

  const [isAddItemDrawerVisible, setIsAddItemDrawerVisible] = useState(false);

  const [localTransfert, setLocalTransfert] = useState(null);
  const [localItems, setLocalItems] = useState(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const prepareTransfertForSave = (transfert) => {
    const { entrepotSource, entrepotDest, ...cleanTransfert } = transfert;
    return {
      ...cleanTransfert,
      CODE_ENT_SRC: cleanTransfert.CODE_ENT_SRC,
      CODE_ENT_DEST: cleanTransfert.CODE_ENT_DEST,
    };
  };

  const handleFieldChange = (field, value) => {
    setLocalTransfert(prevTransfert => ({
      ...prevTransfert,
      [field]: value
    }));
    setHasLocalChanges(true);
  };

  useEffect(() => {
    if (initialTransfert && initialItems) {
      setLocalTransfert({ ...initialTransfert });
      setLocalItems([...initialItems]);
    }
  }, [initialTransfert, initialItems]);

  const handleSave = (row) => {
    setLocalItems((prevItems) =>
      prevItems.map((item) => {
        if (item.CODE_ART === row.CODE_ART) {
          // Map QTE back to QTE_STK for transfert
          const updatedItem = { ...item, ...row };
          if (row.QTE !== undefined) {
            updatedItem.QTE_STK = row.QTE;
            delete updatedItem.QTE;
          }
          return updatedItem;
        }
        return item;
      })
    );
    setHasLocalChanges(true);
  };

  const handleAddItemSuccess = (payload) => {
    const itemsToAdd = Array.isArray(payload) ? payload : [payload];
    setLocalItems((prevItems) => {
      const updated = [...prevItems];
      itemsToAdd.forEach((newItem) => {
        if (!newItem || !newItem.CODE_ART) return;
        const existingIndex = updated.findIndex((it) => it.CODE_ART === newItem.CODE_ART);
        if (existingIndex !== -1) {
          updated[existingIndex] = { ...updated[existingIndex], ...newItem };
        } else {
          updated.push(newItem);
        }
      });
      return updated;
    });
    setHasLocalChanges(true);
    message.success(itemsToAdd.length > 1 ? `${itemsToAdd.length} articles ajoutés` : 'Article ajouté');
  };

  // Transform items for NestedAchatTable: map QTE_STK to QTE
  const transformedItems = useMemo(() => {
    return localItems?.map(item => ({
      ...item,
      QTE: item.QTE_STK || item.QTE || 0
    })) || [];
  }, [localItems]);

  const handleDelete = (codeArt) => {
    setLocalItems((prevItems) => prevItems.filter(item => item.CODE_ART !== codeArt));
    setHasLocalChanges(true);
    message.success("Article supprimé localement. Cliquez sur 'Enregistrer' pour sauvegarder.");
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      const cleanItems = localItems.map(({ article, QTE, ...item }) => ({
        ...item,
        // Ensure QTE_STK is used
        QTE_STK: item.QTE_STK || QTE || 0
      }));

      const data = {
        transfert: prepareTransfertForSave(localTransfert),
        items: cleanItems
      };

      await save(data.transfert, data.items);
      setHasLocalChanges(false);
      if (onChangesSaved) {
        onChangesSaved();
      }
      setIsSaving(false);
      message.success("Transfert enregistré avec succès!");
      refetch();
      refetchItems();
    } catch (error) {
      console.error("Error saving transfert:", error);
      message.error(`Échec de l'enregistrement du transfert: ${error.message || error}`);
      setIsSaving(false);
    }
  };

  const handleValidateClick = async () => {
    try {
      await validate(id);
      message.success('Le transfert a été validé avec succès.');
      refetch();
    } catch (error) {
      message.error(`Échec de la validation du transfert: ${error}`);
    }
  };

  useEffect(() => {
    if (hasLocalChanges && onChangesDetected) {
      onChangesDetected();
    }
  }, [hasLocalChanges, onChangesDetected]);

  const getDescriptionItems = () => [
    {
      key: 'sourceEntrepot',
      label: 'Entrepôt Source',
      children: localTransfert?.entrepotSource ? `${localTransfert.entrepotSource.code_ent} - ${localTransfert.entrepotSource.name}` : localTransfert?.CODE_ENT_SRC,
      span: 2,
    },
    {
      key: 'destEntrepot',
      label: 'Entrepôt Destination',
      children: localTransfert?.entrepotDest ? `${localTransfert.entrepotDest.code_ent} - ${localTransfert.entrepotDest.name}` : localTransfert?.CODE_ENT_DEST,
      span: 2,
    },
    {
      key: 'creationDate',
      label: 'Date Transfert',
      children: (
        <EditableDateField
          value={localTransfert?.DATE_TRF}
          onChange={(newDate) => handleFieldChange("DATE_TRF", newDate)}
          disabled={!!localTransfert?.VALIDER}
        />
      ),
      span: 1,
    },
    {
      key: 'dateValid',
      label: 'Date Validation',
      children: localTransfert?.DATEVALID ? formatDate(localTransfert.DATEVALID) : '-',
      span: 1,
    },
    {
      key: 'heureValid',
      label: 'Heure Validation',
      children: localTransfert?.HEUREVALID ? new Date(localTransfert.HEUREVALID).toLocaleTimeString().slice(0,5) : '-',
      span: 1,
    },
    {
      key: 'remarks',
      label: 'Remarque',
      children: (
        <EditableField
          value={localTransfert?.REMARQUE}
          onChange={(value) => handleFieldChange("REMARQUE", value)}
          userPermissions={userPermissions}
          transfertStatus={localTransfert}
        />
      ),
      span: 2,
    },
  ];

  if (loadingTransfert || loadingItems) return <Spin tip="Loading..." />;
  if (errorTransfert || errorItems)
    return (
      <Alert
        message="Error"
        description={errorTransfert || errorItems}
        type="error"
        showIcon
      />
    );

  if (!localTransfert || !localItems) return <Alert message="Aucun transfert trouvé" type="info" showIcon />;

  const canEdit = currentCan('transfers', 'update') && (userPermissions?.isManager || (!localTransfert.VALIDER && !localTransfert.TRANSFERER));

  // Animation variants
  const containerVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    exit: {
      opacity: 0,
      scale: 0.97,
      transition: {
        duration: 0.12,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      style={{
        transformOrigin: 'center center',
        willChange: 'transform, opacity'
      }}
    >
      <Card
        title={
          <>
            Transfert - {localTransfert.REF_TRF}
            {localTransfert.TRANSFERER ? (
              <Tag color="blue" style={{ marginLeft: "8px" }}>
                Transféré
              </Tag>
            ) : localTransfert.VALIDER ? (
              <Tag color="green" style={{ marginLeft: "8px" }}>
                Validé
              </Tag>
            ) : (
              <Tag color="orange" style={{ marginLeft: "8px" }}>
                Enregistré
              </Tag>
            )}
          </>
        }
        bordered={false}
        extra={
          <Space>
            {hasLocalChanges && (
              <PermissionButton
                icon={<UndoOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Annuler les modifications',
                    content: 'Êtes-vous sûr de vouloir annuler toutes les modifications? Cela rétablira toutes les modifications à l\'état sauvegardé précédemment.',
                    okText: 'Oui, Annuler tout',
                    cancelText: 'Non, Garder les modifications',
                    onOk: () => {
                      setLocalTransfert({ ...initialTransfert });
                      setLocalItems([...initialItems]);
                      setHasLocalChanges(false);
                      message.success('Toutes les modifications ont été annulées');
                    }
                  });
                }}
              >
                Annuler modif.
              </PermissionButton>
            )}
            {!localTransfert.VALIDER && !localTransfert.TRANSFERER && (
              <PermissionButton permissionModule="transfers" permissionAction="validate" type="primary" onClick={handleValidateClick} disabled={hasLocalChanges}>
                Valider Transfert
              </PermissionButton>
            )}
            <PermissionButton permissionModule="transfers" permissionAction="update"
              type="primary"
              onClick={handleSaveChanges}
              disabled={!hasLocalChanges}
              loading={isSaving}
            >
              Enregistrer Modif.
            </PermissionButton>
            <Button
              type="default"
              onClick={onClose}
              style={{
                backgroundColor: 'white',
                color: '#EE2625',
                border: '1px solid #EE2625',
              }}
            >
              SORTIR
            </Button>
          </Space>
        }
        style={{ position: 'relative' }}
      >
        <Descriptions
          bordered
          column={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
          size="small"
          items={getDescriptionItems()}
          labelStyle={{ fontWeight: 'bold', textAlign: 'right' }}
          contentStyle={{ minWidth: '150px' }}
        />
      </Card>

      <Card
        title="Articles dans le transfert"
        style={{ marginTop: 20 }}
        extra={
          canEdit && (
            <PermissionButton permissionModule="transfers" permissionAction="update"
              type="primary"
              onClick={() => setIsAddItemDrawerVisible(true)}
            >
              Ajouter Article
            </PermissionButton>
          )
        }
      >
        <NestedAchatTable
          items={transformedItems}
          handleSave={handleSave}
          handleDelete={handleDelete}
          validated={localTransfert.VALIDER || localTransfert.TRANSFERER}
          userPermissions={userPermissions}
          type="transfert"
        />
      </Card>

      <Drawer
        title="Ajouter un article au transfert"
        width={900}
        onClose={() => setIsAddItemDrawerVisible(false)}
        open={isAddItemDrawerVisible}
      >
        <AddItemToTransfertForm
          refTransfert={localTransfert.REF_TRF}
          sourceEntrepot={localTransfert.CODE_ENT_SRC}
          onSuccess={(items) => {
            handleAddItemSuccess(items);
            setIsAddItemDrawerVisible(false);
          }}
        />
      </Drawer>
    </motion.div>
  );
};

export default SingleTransfert;

